import { __awaiter } from "tslib";
import _ from 'lodash';
import { communicator } from 'lusha-widgets';
import { contactDataPointSearch } from '../../API/contactSearchAPI';
import { mapSingleSearchData } from '../../API/search-v2-mappers';
import { getVersionedSelectors } from '../../hooks/useSearchDataParser/pageListeners';
import { messaging } from '../../lib/messaging';
import { reportDncContactViewedClientEvent } from '../../utils/BIEvents/dnc-event';
import reportClientEvent from '../../utils/BIEvents/reportClientEvent';
import { SEARCH_RESULTS } from '../../utils/constants';
import { actions as appActions } from '../app/reducer';
import CompaniesDispatcher from '../companies/dispatcher';
import { mapSearchCompanyToCompany } from '../companies/mappers';
import ContactsDispatcher from '../contacts/dispatcher';
import { getContactMapKey, mapSearchContactToContact } from '../contacts/mappers';
import { selectHasDncPhone, selectHasOnlyDncPhones, selectTotalDataCollected } from '../contacts/selectors';
import { selectIsAutoOpenEnabled } from '../settings/selectors';
import { actions } from './reducer';
const notifySingleSearchResolved = (contact, state) => {
    const { postMessage: setSearchResult } = communicator.getMessenger('SEARCH_RESULT');
    const { postMessage: openFrame } = communicator.getMessenger('OPEN');
    const totalDataCollected = selectTotalDataCollected(contact);
    const dataExist = totalDataCollected === 0 ? SEARCH_RESULTS.MAX_SEARCH_RESULT : totalDataCollected;
    const { settings, app } = state;
    const isAutoOpenEnabled = selectIsAutoOpenEnabled(state);
    const shouldOpenPlugin = (dataExist === SEARCH_RESULTS.MAX_SEARCH_RESULT || dataExist > 0) && isAutoOpenEnabled;
    setSearchResult({ isRevealed: contact.isRevealed, count: dataExist });
    if (shouldOpenPlugin) {
        openFrame({ openType: 'autoOpen' });
    }
    if (selectHasDncPhone(contact)) {
        reportDncContactViewedClientEvent({
            uniqueSearchId: contact.uniqueSearchId,
            subPlatform: settings.platform,
            pageUrl: app.url,
            numOfContactsWithDncPhone: 1,
            numOfContactsWithOnlyDncPhones: selectHasOnlyDncPhones(contact) ? 1 : 0,
            isBulkSearch: false,
        });
    }
};
const notifySingleSearchRejected = (state) => {
    const { postMessage: setSearchResult } = communicator.getMessenger('SEARCH_RESULT');
    const { settings } = state;
    const selectors = getVersionedSelectors(settings.version, 'profile');
    setSearchResult({ count: -1, pageListeners: selectors[settings.platform] });
};
const applySearchMetadataSideEffects = (state, result) => {
    var _a;
    if ((_a = result === null || result === void 0 ? void 0 : result.metadata) === null || _a === void 0 ? void 0 : _a.isOnboarding) {
        reportClientEvent({
            eventType: 'user_onboarding_demo_search',
            eventValue: state.user.industry,
        }, { enrichData: false });
    }
};
class Dispatcher {
    constructor() {
        this.actions = actions;
    }
    setSearchResult(result) {
        return (dispatch, getState) => {
            dispatch(actions.setSearchResult(result));
            const mappedCompanies = ((result === null || result === void 0 ? void 0 : result.companies) || [])
                .map(mapSearchCompanyToCompany)
                .filter((company) => Boolean(company));
            if (mappedCompanies.length) {
                dispatch(CompaniesDispatcher.setCompanies(mappedCompanies));
            }
            if (!_.isEmpty(result === null || result === void 0 ? void 0 : result.contacts)) {
                const mappedContacts = result.contacts.map(contact => {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
                    return mapSearchContactToContact(contact, {
                        requestId: (_a = result.metadata) === null || _a === void 0 ? void 0 : _a.requestId,
                        maskedId: (_b = result.metadata) === null || _b === void 0 ? void 0 : _b.maskId,
                        companyLid: (_f = (_e = (_d = (_c = result.companies) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.company_lid) !== null && _e !== void 0 ? _e : contact.companyLid) !== null && _f !== void 0 ? _f : null,
                        companyName: ((_g = mappedCompanies[0]) === null || _g === void 0 ? void 0 : _g.name) || ((_j = (_h = result.companies) === null || _h === void 0 ? void 0 : _h[0]) === null || _j === void 0 ? void 0 : _j.name) || '',
                        isOnboarding: Boolean((_k = result.metadata) === null || _k === void 0 ? void 0 : _k.isOnboarding),
                    });
                });
                dispatch(ContactsDispatcher.setContacts(mappedContacts));
                dispatch(actions.setCurrentContactId(getContactMapKey(mappedContacts[0]) || null));
                applySearchMetadataSideEffects(getState(), result);
                notifySingleSearchResolved(mappedContacts[0], getState());
                dispatch(appActions.setIsRefreshingSearch(false));
                return;
            }
            dispatch(actions.setCurrentContactId(null));
            applySearchMetadataSideEffects(getState(), result);
            notifySingleSearchRejected(getState());
        };
    }
    clearSearchResult() {
        return dispatch => {
            dispatch(actions.clearSearchResult());
        };
    }
    /**
     * Runs the single-profile (Enrich) search and stores the result.
     * Replaces the removed MST `Contact.search` action, so both the initial
     * search and the session-expired refresh share one code path.
     */
    runSingleSearch(parserResult) {
        return (dispatch, getState) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            const state = getState();
            const searchData = Object.assign({ request_id: state.contact.requestId, firstSearch: state.contact.firstSearch, sessionId: state.app.sessionId }, parserResult);
            const mappedSearchData = mapSingleSearchData(searchData);
            // goes through core plugin service worker when 3rd party cookies are blocked
            const searchResult = yield contactDataPointSearch(mappedSearchData);
            // @ts-ignore just make sure it's returned from api
            if (!searchResult)
                return null;
            messaging.sendMessage('SEARCH/RESULT', {
                type: 'contact',
                parser: parserResult,
                result: searchResult,
            });
            dispatch(this.setSearchResult(searchResult));
            const contact = (_a = searchResult === null || searchResult === void 0 ? void 0 : searchResult.contacts) === null || _a === void 0 ? void 0 : _a[0];
            if (contact) {
                const hasData = !_.isEmpty(contact.emails) || !_.isEmpty(contact.phones);
                messaging.sendMessage('BADGE/SET_INDICATOR', { count: hasData ? 1 : 0 });
            }
            return searchResult;
        });
    }
}
const ContactDispatcher = new Dispatcher();
export default ContactDispatcher;
